import { useState, useEffect } from 'react';
import { Gift, Flame, Star, Check, X } from 'lucide-react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';

const DailyLoginReward = ({ onCoinsEarned }) => {
  const [streakStatus, setStreakStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => {
    fetchStreakStatus();
  }, []);

  const fetchStreakStatus = async () => {
    try {
      const response = await api.get('/coins/streak-status');
      setStreakStatus(response.data);
    } catch (error) {
      console.error('Error fetching streak status:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimDailyBonus = async () => {
    setClaiming(true);
    try {
      const response = await api.post('/coins/daily-login');
      setClaimResult(response.data);
      setShowSuccess(true);
      
      // Refresh streak status
      await fetchStreakStatus();
      
      // Notify parent of coins earned
      if (onCoinsEarned) {
        onCoinsEarned(response.data.coins_earned);
      }
      
      // Auto-hide success after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error claiming daily bonus:', error);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 animate-pulse">
        <div className="h-20 bg-white/20 rounded"></div>
      </div>
    );
  }

  if (!streakStatus) return null;

  const { current_streak, longest_streak, can_claim_today, daily_reward, next_milestone, next_milestone_bonus, days_to_milestone } = streakStatus;

  return (
    <div className="relative">
      {/* Success Animation Overlay */}
      {showSuccess && claimResult && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 rounded-xl">
          <div className="bg-white rounded-xl p-6 text-center animate-bounce shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-green-600 mb-1">+{claimResult.coins_earned} Coins!</h3>
            <p className="text-gray-600 text-sm">{claimResult.message}</p>
            {claimResult.streak_bonus > 0 && (
              <p className="text-amber-600 font-semibold mt-2">
                🎉 Streak bonus: +{claimResult.streak_bonus}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 shadow-lg">
        <div className="flex items-center justify-between">
          {/* Left side - Streak info */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Flame className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-xl">{current_streak} Day Streak</span>
                {current_streak > 0 && <span className="text-yellow-200">🔥</span>}
              </div>
              <p className="text-white/80 text-sm">
                {days_to_milestone > 0 
                  ? `${days_to_milestone} days to ${next_milestone}-day bonus (+${next_milestone_bonus} coins)`
                  : `Next milestone at ${next_milestone} days`
                }
              </p>
              {longest_streak > current_streak && (
                <p className="text-white/60 text-xs">Best: {longest_streak} days</p>
              )}
            </div>
          </div>

          {/* Right side - Claim button */}
          <div className="text-center">
            {can_claim_today ? (
              <Button
                onClick={claimDailyBonus}
                disabled={claiming}
                className="bg-white hover:bg-gray-100 text-orange-600 font-bold px-6 py-3 rounded-lg shadow-lg transform hover:scale-105 transition-all"
              >
                {claiming ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                    Claiming...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Claim +{daily_reward}
                  </span>
                )}
              </Button>
            ) : (
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <Check className="w-6 h-6 text-white mx-auto" />
                <p className="text-white text-sm font-medium">Claimed Today!</p>
              </div>
            )}
          </div>
        </div>

        {/* Streak milestones preview */}
        <div className="mt-4 pt-3 border-t border-white/20">
          <div className="flex justify-between items-center">
            {[3, 7, 30].map((milestone) => (
              <div 
                key={milestone} 
                className={`flex items-center gap-1 ${current_streak >= milestone ? 'text-yellow-200' : 'text-white/50'}`}
              >
                <Star className={`w-4 h-4 ${current_streak >= milestone ? 'fill-yellow-200' : ''}`} />
                <span className="text-xs font-medium">{milestone}d</span>
              </div>
            ))}
          </div>
          <div className="mt-2 bg-white/20 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-yellow-300 h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min((current_streak / 30) * 100, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyLoginReward;
